import { Aggregator, IScrBaseResponse } from "../services";
import { BillingOrder } from "./billingorder.model";
import { Partner } from "./partner.model";
import { StringOrPhoneNumber } from "./common.model";
import { ApplicationType, CampaignType, LeaseStatus, LeaseType, PoliticalCampaignStatus, SelectType, StatusReason } from "./constants";
import { Field, ObjectType } from "./decorators";
import { DiscountSummary } from "./discount.model";
import { Transfer } from "./shortcodes.model";


/**
 * Match the lease class from server-side tscommon library.
 */

@ObjectType()
export class Lease {

  @Field({ key: true, q: ['summary', 'sdlist'] }) leaseId!: number;
  @Field({ q: ['summary', 'sdlist'] }) public orderId!: number;
  @Field({ q: ['summary', 'sdlist'] }) public scId!: number;
  @Field({ q: ['summary', 'sdlist'] }) public accountId?: number;
  @Field() public company?: string;
  @Field({ q: ['summary', 'sdlist'] }) public latestLease?: boolean;
  @Field({ q: ['sdlist'] }) public applicationType?: ApplicationType;
  @Field({ q: ['summary', 'sdlist'] }) public leaseType!: LeaseType;
  @Field({ q: ['summary', 'sdlist'] }) public term?: number;
  @Field({ q: ['summary', 'sdlist'] }) public autorenew?: boolean;
  @Field({ q: ['summary', 'sdlist'] }) public selectType?: SelectType;
  @Field({ q: ['summary', 'sdlist'] }) public leaseStatus!: LeaseStatus;
  @Field({ q: ['summary', 'sdlist'] }) public statusReason?: StatusReason;
  @Field({ q: ['summary', 'sdlist'] }) public previousStatus?: LeaseStatus;
  @Field({ q: ['sdlist'] }) public scExpirationDate!: string;
  @Field() public approvalDate?: string;
  @Field() public deactivateDate?: string;
  @Field() public reactivateDate?: string;
  @Field({ q: ['summary', 'sdlist'] }) public ageDate?: string;
  @Field() public createTime?: string;
  @Field({ q: ['summary', 'sdlist'] }) public startDate?: string;
  @Field({ q: ['summary', 'sdlist'] }) public endDate?: string;
  @Field() public parentOrderId?: number;
  @Field() public originalOrderId?: number;
  @Field({ q: ['summary', 'sdlist'] }) public mbOptOutDate?: string;
  @Field({ q: ['political', 'sdlist'] }) @Field() public forPoliticalCampaign?: boolean;
  @Field({ q: ['political', 'sdlist'] }) @Field() public fecId?: string;
  @Field({ q: ['political', 'sdlist'] }) @Field() public politicalCampaignType?: CampaignType;
  @Field({ q: ['political', 'sdlist'] }) @Field() public politicalCampaignInfo?: string;
  @Field({ q: ['political', 'sdlist'] }) @Field() public politicalCampaignStatus?: PoliticalCampaignStatus;
  @Field({ q: ['political', 'sdlist'] }) @Field() public politicalCampaignComments?: string;
  @Field({ q: ['summary', 'sdlist'] }) public price!: number;   // price = list_price minus discount
  @Field() public listPrice?: number;
  @Field() public discountAmount?: number;
  @Field() public discountList?: DiscountSummary[];
  @Field() public description?: string;
  @Field() public unitPrice?: number;
  @Field() public quantity?: number;
  @Field() public addToMb?: boolean;
  @Field() public convertToRandom?: boolean;
  @Field() public suppressExpiryEmail?: boolean;
  @Field({ q: ['summary', 'sdlist'] }) public orderSource?: string;
  @Field() public orderInitiator?: string;

  @Field() public firstName?: string;
  @Field() public lastName?: string;
  @Field() public address1?: string;
  @Field() public address2?: string;
  @Field() public city?: string;
  @Field() public state?: string;
  @Field() public zip?: string;
  @Field() public country?: string;
  @Field() public phone?: StringOrPhoneNumber;
  @Field() public phoneExtension?: string;
  @Field() public email?: string;
  @Field() public billingFirstName?: string;
  @Field() public billingLastName?: string;
  @Field() public billingAddress1?: string;
  @Field() public billingAddress2?: string;
  @Field() public billingCity?: string;
  @Field() public billingState?: string;
  @Field() public billingZip?: string;
  @Field() public billingCountry?: string;
  @Field() public billingPhone?: StringOrPhoneNumber;
  @Field() public billingPhoneExtension?: string;
  @Field() public billingEmail?: string;

  @Field() public cpId?: number;

  @Field() public aggregatorId?: number;
  @Field() public applicationProvider?: string;
  @Field() public updateTime?: Date;
  @Field() public misc?: string;
  @Field() public registryComments?: string;
  @Field() public internalComments?: string;

  @Field() public pricingHash?: string;

  // short code hash used to re-instantiate the marketing brochure local storage
  @Field() public shortCodeHash?: string;

  //additions for 9.2 related to brands
  @Field() public demoCode?: boolean;
  @Field() public parkedCode?: boolean;

  // not @Field - do not query with GraphQL automatically.
  public brands?: Partner[];
  public brandIds?: number[];
  public contentProvider?: Partner;
  public billingOrder?: BillingOrder;
  public aggregator?: Aggregator;


  /* client side extensions */

  // if user searched for a short code by an alpha numeric, it is returned.
  @Field() public ANString?: string;
  @Field() public selected?: boolean;

  public termXm?: string;
  public priceTBD?: boolean;
  public originalDemoCode: boolean | undefined;
  public originalParkedCode: boolean | undefined;
  public positionInItemList?: number;

  // for sd
  aggregatorName?: string;
  comment?: string;
}

export interface CodeAndHash {
  code: number;
  hash: string;
}

export interface LeaseSummaryResponse extends IScrBaseResponse {
  data: {
    leases: LeaseSummary[];
    page: string;
    limit: number;
    totalCount: number;
  }
}

/**
 * Summary of values in a lease returned by short code list API.
 */
export interface LeaseSummary {
  orderId: number;
  scId: number;
  leaseType: string;
  term: number;
  autorenew: boolean;
  leaseStatus: string;
  scExpirationDate: string;
  statusReason?: string;
  leaseId?: number;
  linkedAccountName?: string;
  accountId?: number;
  parkedCode: boolean | undefined;

  // client side extensions
  details?: LeasedCodeDetail;
  mbOptOutDate?: string;             // assigned from details.
  orderSource: string | undefined;   // assigned from details.

  settings: LeaseSummaryClientSettings;
  termXm: string | undefined;
  leaseStatusXm: string | undefined;
}

export interface LeaseSummaryClientSettings {
  label?: string,
  showButton?: boolean,
  clickAction?: string;
}



export interface LeasedCodeDetail {
  lease: Lease;
  transfer?: Transfer;
  parentInvoiceId?: number;
  parentInvoiceDocumentId?: number;
  parentReceiptDocumentId?: number;
  parentLeaseType?: string;
  parentPaymentId?: number;
  parentLeaseMbOptOutDate?: string;
  monthlyInvoiceDocumentId?: number;
  monthlyReceiptDocumentId?: number;
  monthlyInvoiceId?: number;
  parentMonthlyInvoiceId?: number;
  parentMonthlyReceiptDocumentId?: number;
}


/*
export class ShortCodeLeaseDetails extends Lease {
  public transferStatus?: string;
  public toAccountId?: number;
  public transferId?: number;
  public name?: string; // aggregator name
  public paymentId?: number; // was order paid in multi-payment flow?
  public invoiceDocumentId?: number;
  public receiptDocumentId?: number;

  public monthlyReceiptDocumentId?: number;
  public monthlyInvoiceDocumentId?: number;
  public monthlyInvoiceId?: number;

  public bulkReceiptDocumentId?: number;

  public parentInvoiceId?: number;
  public parentInvoiceDocumentId?: number;
  public parentReceiptDocumentId?: number;
  public parentLeaseType?: OrderType;
  public parentPaymentId?: number;
  public parentMonthlyReceiptDocumentId?: number;
  public parentMonthlyInvoiceId?: number;
  public parentLeaseMbOptOutDate?: Date;

}


export interface ShortCodeLeaseDetailsHolder {
  leasedCodeDetail: ShortCodeLeaseDetails;
}
*/

/**
 * Edit billing billing details has a different subset up lease data.
 * Define that here.
 */
export interface LeaseBilling {
  orderId: number;
  scList: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  billingCountry: string;
  billingPhone: StringOrPhoneNumber;
  billingPhoneExtension?: string;
  billingEmail: string;
  billingFirstName: string;
  billingLastName: string;
  billingAddress1: string;
  billingAddress2: string;
  isInvoiceUpdate: boolean;
}
